import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import Logo from './Logo';
import { WA_DEFAULT_LINK } from '../lib/lead';

const LINKS = [
  { label: 'How It Works', id: 'how-it-works' },
  { label: 'Services', id: 'services' },
  { label: 'Why Us', id: 'why-us' },
  { label: 'FAQs', id: 'faqs' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goToSection = (id) => {
    setOpen(false);
    const scroll = () => {
      const el = document.getElementById(id);
      if (window.__lenis && el) window.__lenis.scrollTo(el, { offset: -80 });
      else if (el) el.scrollIntoView({ behavior: 'smooth' });
    };
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(scroll, 450);
    } else {
      scroll();
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[68px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="header-logo-link" aria-label="Settalkar home" className="shrink-0">
          <Logo size={40} />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => goToSection(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="text-sm font-semibold text-slate-600 hover:text-brand-blue transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-0.5 w-0 bg-brand-orange group-hover:w-full transition-all duration-300" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={WA_DEFAULT_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-cta"
            className="hidden sm:inline-flex items-center gap-2 rounded-xl bg-brand-orange hover:bg-brand-orangedark text-white text-sm font-bold px-5 py-2.5 shadow-lg shadow-orange-500/25 transition-all hover:-translate-y-0.5 active:translate-y-0"
          >
            Check Challan on WhatsApp
            <ArrowUpRight size={16} strokeWidth={2.5} />
          </a>
          <button
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-xl border border-slate-200 text-slate-700 bg-white"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="header-mobile-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            className="lg:hidden overflow-hidden border-t border-slate-100 bg-white"
            aria-label="Mobile"
          >
            <div className="px-4 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => goToSection(l.id)}
                  className="text-left px-4 py-3 rounded-xl text-base font-semibold text-slate-700 hover:bg-slate-50 hover:text-brand-blue transition-colors"
                  data-testid={`mobile-nav-link-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
              <a
                href={WA_DEFAULT_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-xl bg-brand-orange text-white font-bold px-5 py-3.5"
                data-testid="mobile-header-whatsapp-cta"
              >
                Check Challan on WhatsApp
                <ArrowUpRight size={18} strokeWidth={2.5} />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
